import { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import type { UserRow, UsersResponse } from '@/lib/types';
import { Avatar } from '@/components/Avatar';
import { useDepartments } from '@/lib/queries';

type SortField =
  | 'full_name'
  | 'upn'
  | 'business_line'
  | 'department'
  | 'has_license'
  | 'last_activity_date'
  | 'prompts_total'
  | 'prompts_chat_work'
  | 'prompts_chat_web'
  | 'active_days';

export type PeriodDays = 7 | 30 | 90;

type SortDir = 'asc' | 'desc';

export interface SortState {
  field: SortField;
  dir: SortDir;
}

export interface FiltersState {
  q: string;
  department: string | null;
  hasLicense: boolean | null;
  onlyInactive: boolean;
}

interface Props {
  data: UsersResponse | undefined;
  loading: boolean;
  page: number;
  pageSize: number;
  onPageChange: (p: number) => void;
  filters: FiltersState;
  onFiltersChange: (f: FiltersState) => void;
  onSelectUser: (upn: string) => void;
  sort: SortState;
  onSortChange: (s: SortState) => void;
  businessLine: string | null;
  period: PeriodDays;
  onPeriodChange: (p: PeriodDays) => void;
}

function fmtNum(n: number): string {
  if (n === 0) return '—';
  return n.toLocaleString('es-ES');
}

function fmtDate(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function statusColor(user: UserRow): string {
  if (!user.has_license) return 'bg-ink-700';
  if (user.any_app_used_30d) return 'bg-emerald-500';
  return 'bg-red-500';
}

function statusTitle(user: UserRow): string {
  if (!user.has_license) return 'Sin licencia';
  if (user.any_app_used_30d) return 'Activo en 30d';
  return 'Licencia sin uso';
}

function AvatarWithStatus({ user }: { user: UserRow }) {
  return (
    <div className="relative">
      <Avatar upn={user.upn} fullName={user.full_name} size="sm" />
      <span
        title={statusTitle(user)}
        className={`absolute -bottom-0 -right-0 inline-block h-2.5 w-2.5 rounded-full ring-2 ring-ink-950 ${statusColor(
          user,
        )}`}
      />
    </div>
  );
}

function SortHeader({
  label,
  field,
  sort,
  onChange,
}: {
  label: string;
  field: SortField;
  sort: SortState;
  onChange: (s: SortState) => void;
}) {
  const active = sort.field === field;
  const nextDir: SortDir = active && sort.dir === 'asc' ? 'desc' : 'asc';
  return (
    <button
      type="button"
      onClick={() => onChange({ field, dir: nextDir })}
      className={`flex w-full items-center gap-1.5 px-6 py-2 text-[0.65rem] uppercase tracking-widest transition-colors ${
        active ? 'text-ink-200' : 'text-ink-500 hover:text-ink-300'
      }`}
    >
      <span>{label}</span>
      <span
        aria-hidden="true"
        className={`text-[0.6rem] ${active ? 'text-ink-300' : 'text-ink-700'}`}
      >
        {active ? (sort.dir === 'asc' ? '↑' : '↓') : '↕'}
      </span>
    </button>
  );
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function ExportButton({
  buildUrl,
  disabled,
}: {
  buildUrl: () => string;
  disabled: boolean;
}) {
  const { instance } = useMsal();
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
      if (!account) return;
      const { accessToken } = await instance.acquireTokenSilent({
        scopes: [import.meta.env.VITE_API_SCOPE],
        account,
      });
      const r = await fetch(`${import.meta.env.VITE_API_BASE_URL}${buildUrl()}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const cd = r.headers.get('content-disposition') ?? '';
      const m = /filename="([^"]+)"/.exec(cd);
      const filename = m ? m[1]! : 'copilot-usage.csv';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={() => void download()}
      disabled={disabled || busy}
      className="flex items-center gap-1.5 border border-ink-700 px-3 py-1.5 text-xs text-ink-200 transition-colors hover:bg-ink-900 disabled:opacity-50"
    >
      <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
        <path
          d="M6 1v7M3 5l3 3 3-3M2 10h8"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {busy ? 'Exportando…' : 'Exportar CSV'}
    </button>
  );
}

export function UsersTable({
  data,
  loading,
  page,
  pageSize,
  onPageChange,
  filters,
  onFiltersChange,
  onSelectUser,
  sort,
  onSortChange,
  businessLine,
  period,
  onPeriodChange,
}: Props) {
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const [searchInput, setSearchInput] = useState(filters.q);
  const debouncedSearch = useDebounced(searchInput, 250);
  useEffect(() => {
    if (debouncedSearch !== filters.q) {
      onFiltersChange({ ...filters, q: debouncedSearch });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const departments = useDepartments(businessLine);

  const buildExportUrl = () => {
    const params = new URLSearchParams();
    if (businessLine) params.set('business_line', businessLine);
    if (filters.q.trim()) params.set('q', filters.q.trim());
    if (filters.department) params.set('department', filters.department);
    if (filters.hasLicense === true) params.set('has_license', 'true');
    else if (filters.hasLicense === false) params.set('has_license', 'false');
    if (filters.onlyInactive) params.set('only_inactive', 'true');
    params.set('sort_by', sort.field);
    params.set('sort_dir', sort.dir);
    params.set('period_days', String(period));
    return `/api/users/export.csv?${params.toString()}`;
  };

  const activeFilters =
    (filters.q ? 1 : 0) +
    (filters.department ? 1 : 0) +
    (filters.hasLicense !== null ? 1 : 0) +
    (filters.onlyInactive ? 1 : 0);

  return (
    <section className="panel">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-ink-800 px-6 py-4">
        <div>
          <div className="label">Equipo</div>
          <h2 className="mt-1 text-lg font-light text-ink-50">
            Empleados y su estado de Copilot
            {activeFilters > 0 ? (
              <span className="ml-2 text-xs text-ink-500">
                · {total} resultado{total === 1 ? '' : 's'}
              </span>
            ) : null}
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <div
            role="tablist"
            aria-label="Periodo"
            className="flex border border-ink-800 bg-ink-950"
          >
            {([7, 30, 90] as const).map((d) => (
              <button
                key={d}
                type="button"
                role="tab"
                aria-selected={period === d}
                onClick={() => onPeriodChange(d)}
                className={`px-2.5 py-1 text-xs tabular-nums transition-colors ${
                  period === d
                    ? 'bg-ink-100 text-ink-950'
                    : 'text-ink-400 hover:text-ink-200'
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
          <ExportButton buildUrl={buildExportUrl} disabled={loading} />
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-3 border-b border-ink-800/60 px-6 py-3">
        <div className="relative min-w-[14rem] flex-1">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-600">
            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
              <circle cx="6" cy="6" r="4.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
              <path d="M9.5 9.5l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </span>
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Buscar por nombre o email…"
            className="w-full border border-ink-800 bg-ink-900 py-1.5 pl-9 pr-3 text-sm text-ink-100 placeholder:text-ink-600 focus:border-ink-500 focus:outline-none"
          />
        </div>

        <select
          value={filters.department ?? ''}
          onChange={(e) =>
            onFiltersChange({
              ...filters,
              department: e.target.value || null,
            })
          }
          className="border border-ink-800 bg-ink-900 px-3 py-1.5 text-sm text-ink-100 focus:border-ink-500"
        >
          <option value="">Todos los departamentos</option>
          {(departments.data?.items ?? []).map((d: string) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>

        <select
          value={filters.hasLicense === null ? '' : filters.hasLicense ? 'yes' : 'no'}
          onChange={(e) => {
            const v = e.target.value;
            onFiltersChange({
              ...filters,
              hasLicense: v === '' ? null : v === 'yes',
            });
          }}
          className="border border-ink-800 bg-ink-900 px-3 py-1.5 text-sm text-ink-100 focus:border-ink-500"
        >
          <option value="">Con o sin licencia</option>
          <option value="yes">Con licencia</option>
          <option value="no">Sin licencia</option>
        </select>

        <label className="flex cursor-pointer items-center gap-2 text-sm text-ink-300">
          <input
            type="checkbox"
            checked={filters.onlyInactive}
            onChange={(e) =>
              onFiltersChange({ ...filters, onlyInactive: e.target.checked })
            }
            className="h-4 w-4 accent-ink-300"
          />
          Solo licencia sin uso 30d
        </label>

        {activeFilters > 0 ? (
          <button
            type="button"
            onClick={() => {
              setSearchInput('');
              onFiltersChange({
                q: '',
                department: null,
                hasLicense: null,
                onlyInactive: false,
              });
            }}
            className="text-xs text-ink-400 underline-offset-4 hover:underline"
          >
            Limpiar filtros
          </button>
        ) : null}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-ink-800/60 text-left">
              <th className="w-12 px-6" />
              <th className="font-normal">
                <SortHeader label="Nombre" field="full_name" sort={sort} onChange={onSortChange} />
              </th>
              <th className="font-normal">
                <SortHeader label="Departamento" field="department" sort={sort} onChange={onSortChange} />
              </th>
              <th className="font-normal">
                <SortHeader label="Licencia" field="has_license" sort={sort} onChange={onSortChange} />
              </th>
              <th className="font-normal">
                <SortHeader
                  label="Última actividad"
                  field="last_activity_date"
                  sort={sort}
                  onChange={onSortChange}
                />
              </th>
              <th className="font-normal">
                <SortHeader
                  label="Prompts"
                  field="prompts_total"
                  sort={sort}
                  onChange={onSortChange}
                />
              </th>
              <th className="font-normal">
                <SortHeader
                  label="Chat trabajo"
                  field="prompts_chat_work"
                  sort={sort}
                  onChange={onSortChange}
                />
              </th>
              <th className="font-normal">
                <SortHeader
                  label="Chat web"
                  field="prompts_chat_web"
                  sort={sort}
                  onChange={onSortChange}
                />
              </th>
              <th className="font-normal">
                <SortHeader
                  label="Días activos"
                  field="active_days"
                  sort={sort}
                  onChange={onSortChange}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-6 py-12 text-center text-ink-500">
                  Cargando…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-6 py-12 text-center text-ink-500">
                  Sin resultados
                </td>
              </tr>
            ) : (
              items.map((u) => (
                <tr
                  key={u.upn}
                  onClick={() => onSelectUser(u.upn)}
                  className="cursor-pointer border-b border-ink-800/40 hover:bg-ink-900/60"
                >
                  <td className="py-2 pl-6 pr-2">
                    <AvatarWithStatus user={u} />
                  </td>
                  <td className="px-6 py-2">
                    <div className="text-ink-100">{u.full_name}</div>
                    <div className="text-xs text-ink-500">{u.upn}</div>
                  </td>
                  <td className="px-6 py-2 text-ink-300">{u.department ?? '—'}</td>
                  <td className="px-6 py-2 text-ink-300">{u.has_license ? 'Sí' : 'No'}</td>
                  <td className="px-6 py-2 tabular-nums text-ink-300">
                    {fmtDate(u.last_activity_date)}
                  </td>
                  <td className="px-6 py-2 tabular-nums text-right text-ink-100">
                    {fmtNum(u.prompts_total)}
                  </td>
                  <td className="px-6 py-2 tabular-nums text-right text-ink-300">
                    {fmtNum(u.prompts_chat_work)}
                  </td>
                  <td className="px-6 py-2 tabular-nums text-right text-ink-300">
                    {fmtNum(u.prompts_chat_web)}
                  </td>
                  <td className="px-6 py-2 tabular-nums text-right text-ink-300">
                    {u.active_days === 0 ? '—' : u.active_days}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <footer className="flex items-center justify-between border-t border-ink-800 px-6 py-3 text-xs text-ink-500">
        <div>
          {total === 0
            ? '0'
            : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)}`}{' '}
          de {total}
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            disabled={page <= 1 || loading}
            onClick={() => onPageChange(page - 1)}
            className="px-2 py-1 text-ink-300 disabled:text-ink-700"
          >
            ← Anterior
          </button>
          <span className="tabular-nums">
            {page} / {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages || loading}
            onClick={() => onPageChange(page + 1)}
            className="px-2 py-1 text-ink-300 disabled:text-ink-700"
          >
            Siguiente →
          </button>
        </div>
      </footer>
    </section>
  );
}
